import { createContext, useContext, useMemo } from "react";
import { validToken, YStack } from "@medbillai/ui";
import { BackgroundRadialGrantedGradient } from "../BackgroundRadialGrantedGradient";
import { ContentBody, FooterStrip, type OnboardingContentProps, OnboardingDisclaimer, type OnboardingFooterProps, OnboardingFormError, OnboardingHeader, OnboardingNav, OnboardingPrimaryAction, type OnboardingScreenProps, OnboardingSecondaryAction, OnboardingTertiaryAction, OnboardingToneContext, TONE_BACKGROUND, TONE_BLEED_GUARD } from "./OnboardingScreen.shared";

// Web mechanics: the screen behaves like a normal scrolling web page, not a
// height-capped app shell. At phone widths the root is *at least* the
// visible viewport tall (min-height:100dvh) and grows with its content — so
// when a resize-model browser (Chrome iOS) shrinks the web view for its
// keyboard, nothing above the footer reflows; the page just becomes taller
// than the view and the browser reveals the focused input by ordinary page
// scrolling. A capped height:100dvh variant instead reflows the whole
// screen at keyboard-open. Above $gtSm the desktop hero shell stays capped
// and content scrolls internally. At phone widths, the 100dvh floor keeps the
// footer at the visible bottom while the page remains free to grow with content.

// Compound-component wiring for the `pinnedFooter` and `contained` props:
// Content and Footer are the caller's own elements, so Root can only reach
// them via context. Private to this file — callers use the props.
const LayoutContext = createContext({
  pinnedFooter: true,
  contained: false
});

// Ends 8px short of the footer, so scrolled content never runs into the
// button. Must stay shorter than the `$sp6` bottom spacer: at rest the faded
// strip is that spacer, so a screen that fits never dims its last line.
const SCROLL_EDGE_FADE_MASK = "linear-gradient(to bottom, black calc(100% - 28px), transparent calc(100% - 8px))";

/**
 * Content region. At phone widths the page is the scroll surface and, when
 * pinned, this region absorbs spare viewport height so the footer lands on
 * the visible bottom; above $gtSm, or when `contained`, it scrolls internally
 * instead.
 *
 * The scroll container and the width cap are separate boxes: the scroller
 * spans the window so a wheel anywhere in the desktop margins reaches it,
 * while the inner column holds the reading measure.
 */
function OnboardingContent({
  children
}: OnboardingContentProps) {
  const {
    pinnedFooter,
    contained
  } = useContext(LayoutContext);
  return <YStack width="100%" flexGrow={pinnedFooter ? 1 : 0} overflowY={contained ? "auto" : undefined} maskImage={contained ? SCROLL_EDGE_FADE_MASK : undefined} $gtSm={{
    flexGrow: 0,
    maxHeight: "100%",
    overflowY: "auto",
    maskImage: SCROLL_EDGE_FADE_MASK
  }} flexShrink={1} flexBasis="auto" minHeight={0} data-sentry-element="YStack" data-sentry-component="OnboardingContent" data-sentry-source-file="OnboardingScreen.tsx">
      <YStack width="100%" maxWidth={456} alignSelf="center"
    // Mirrors the scroller's flex so a screen with its own internal scroll
    // region still gets shrink pressure through this wrapper.
    flexGrow={pinnedFooter ? 1 : 0} $gtSm={{
      flexGrow: 0
    }} flexShrink={1} flexBasis="auto" minHeight={0} data-sentry-element="YStack" data-sentry-source-file="OnboardingScreen.tsx">
        <ContentBody flushBottom data-sentry-element="ContentBody" data-sentry-source-file="OnboardingScreen.tsx">
          {children}
          {/* A spacer rather than padding: when Content scrolls, ContentBody
              shrinks to the scroller and its children overflow it, so padding
              would fall outside the scroll extent and the last line would stay
              under the fade. */}
          <YStack paddingTop="$sp6" flexShrink={0} data-sentry-element="YStack" data-sentry-source-file="OnboardingScreen.tsx" />
        </ContentBody>
      </YStack>
    </YStack>;
}
function OnboardingFooter({
  children,
  isFading
}: OnboardingFooterProps) {
  const {
    pinnedFooter
  } = useContext(LayoutContext);
  return <YStack width="100%" maxWidth={456} alignSelf="center"
  // Pinned footers sit on the page's bottom edge — keep a gap above the
  // browser chrome / home indicator. The desktop shell is capped, so its
  // footer lands there too.
  paddingBottom={pinnedFooter ? "$sp2" : undefined} $gtSm={{
    paddingBottom: "$sp2"
  }} data-sentry-element="YStack" data-sentry-component="OnboardingFooter" data-sentry-source-file="OnboardingScreen.tsx">
      <FooterStrip isFading={isFading} data-sentry-element="FooterStrip" data-sentry-source-file="OnboardingScreen.tsx">{children}</FooterStrip>
    </YStack>;
}
function OnboardingScreenRoot({
  showBackgroundGradient,
  pinnedFooter = true,
  contained = false,
  tone = "light",
  children
}: OnboardingScreenProps) {
  const layout = useMemo(() => ({
    pinnedFooter,
    contained
  }), [pinnedFooter, contained]);
  return <OnboardingToneContext.Provider value={tone} data-sentry-element="OnboardingToneContext.Provider" data-sentry-component="OnboardingScreenRoot" data-sentry-source-file="OnboardingScreen.tsx">
      <LayoutContext.Provider value={layout} data-sentry-element="LayoutContext.Provider" data-sentry-source-file="OnboardingScreen.tsx">
        <YStack flex={1} backgroundColor={TONE_BACKGROUND[tone]}
      // Covers the sub-pixel clip seam at the right edge.
      boxShadow={TONE_BLEED_GUARD[tone]}
      // Content-sized so height propagates through the auto-height
      // transition wrappers at phone widths.
      flexBasis="auto"
      // Floor, not a cap: content absorbs spare height and the footer lands
      // on the visible bottom. CSS-only so SSR'd desktop never carries it.
      minHeight={pinnedFooter && !contained ? validToken("100dvh") : undefined}
      // A definite height instead, so Content (or a scroller inside it)
      // can shrink to the space between header and footer.
      height={contained ? validToken("100dvh") : undefined}
      // Zero, not `auto`: `min-height: auto` would floor the root at its own
      // content height and grow past the desktop shell's cap.
      $gtSm={{
        minHeight: 0,
        height: "auto"
      }} data-sentry-element="YStack" data-sentry-source-file="OnboardingScreen.tsx">
          {showBackgroundGradient ? <BackgroundRadialGrantedGradient fullscreen /> : null}
          {children}
        </YStack>
      </LayoutContext.Provider>
    </OnboardingToneContext.Provider>;
}
export const OnboardingScreen = Object.assign(OnboardingScreenRoot, {
  Nav: OnboardingNav,
  Header: OnboardingHeader,
  Content: OnboardingContent,
  Footer: OnboardingFooter,
  FormError: OnboardingFormError,
  PrimaryAction: OnboardingPrimaryAction,
  SecondaryAction: OnboardingSecondaryAction,
  TertiaryAction: OnboardingTertiaryAction,
  Disclaimer: OnboardingDisclaimer
});
export type { OnboardingHeaderProps, OnboardingNavProps, OnboardingPrimaryActionProps, OnboardingScreenProps } from "./OnboardingScreen.shared";
export { computeOpenedOffset, partitionFooterChildren } from "./OnboardingScreen.shared";